import { useState } from "react";

import { BreadcrumbProps, Breadcrumbs, Classes } from "@blueprintjs/core";
import styled from "@emotion/styled";
import { Emoji, EmojiStyle } from "emoji-picker-react";
import { Link } from "react-router";

import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { Media } from "@/components/layout/Media";
import { useAuth } from "@/components/lib/auth";
import { AssignScenarioDialog } from "@/components/pages_logged_in/roleplays/AssignScenarioDialog";
import { CreateCollectionDialog } from "@/components/pages_logged_in/roleplays/CreateCollectionDialog";
import { buildRoleplayUrl } from "@/components/pages_logged_in/roleplays/utils";
import { Container } from "@/components/pieces/bootstrap";
import { CloneCollectionDialog } from "@/components/pieces/roleplays/CloneCollectionDialog";
import { RoleplayDeleteConfirm } from "@/components/pieces/roleplays/RoleplayDeleteConfirm";
import { RoleplayLimitBanner } from "@/components/pieces/roleplays/RoleplayLimitBanner";
import { RoleplayMenu } from "@/components/pieces/roleplays/RoleplayMenu";
import { RoleplayShare } from "@/components/pieces/roleplays/RoleplayShare";
import { Button } from "@/components/ui/button";
import { SUBTEXT_COLOR } from "@/css/constants";
import { FullRoleplay, RoleplayModal } from "@/types/roleplays";

const ROLEPLAY_DASHBOARD_ROUTE = {
  href: "/roleplays",
  text: "Roleplay Dashboard",
};

interface CollectionHeaderProps {
  roleplay?: FullRoleplay | null;
  loading?: boolean;
  noMargins?: boolean;
}

export function CollectionHeader({
  noMargins = false,
  roleplay,
  loading = false,
}: CollectionHeaderProps) {
  const [currentActiveModal, setCurrentActiveModal] = useState<RoleplayModal>();
  const { currentWorkspace } = useAuth();
  const [assignScenarioOpen, setAssignScenarioOpen] = useState(false);
  const isAdmin = isWorkspaceAdmin();
  const breadcrumbs: BreadcrumbProps[] = [
    ROLEPLAY_DASHBOARD_ROUTE,
    { text: "Collection Overview" },
  ];

  function breadcrumbRenderer(props: BreadcrumbProps) {
    if (loading) {
      return <div className={`${Classes.SKELETON} h-5 w-[83px]`} />;
    }
    return (
      <StyledBreadcrumb title={String(props.text)}>
        {props.href ? <Link to={props.href}>{props.text}</Link> : props.text}
      </StyledBreadcrumb>
    );
  }

  const hasIcon = Boolean(roleplay?.emojiUnicode) || loading;

  return (
    <>
      <div className="bbs bg-white py-8">
        <Container style={noMargins ? { margin: "revert" } : {}}>
          <div className={noMargins ? "" : "mb-2"}>
            <Breadcrumbs
              items={breadcrumbs}
              breadcrumbRenderer={breadcrumbRenderer}
            />
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-2 sm:gap-3 min-w-0">
              {hasIcon ? (
                <Media greaterThan="xs">
                  {loading || !roleplay ? (
                    <div
                      className={`${Classes.SKELETON} h-[33px] w-[26px] flex-shrink-0`}
                    />
                  ) : (
                    <div className="flex-shrink-0">
                      <Emoji
                        unified={roleplay.emojiUnicode.toLowerCase()}
                        emojiStyle={EmojiStyle.APPLE}
                        size={26}
                      />
                    </div>
                  )}
                </Media>
              ) : null}
              <div className="w-full min-w-0">
                <div
                  className={`font-semibold text-2xl leading-none ${
                    loading ? Classes.SKELETON : ""
                  }`}
                  style={{
                    width: loading ? "400px" : "auto",
                    height: loading ? "33px" : "auto",
                    maxWidth: "100%",
                  }}
                >
                  {!loading && roleplay && (
                    <Link
                      className="color-inherit"
                      to={buildRoleplayUrl(roleplay.slug)}
                    >
                      <h1 className="m-0 text-xl sm:text-2xl md:text-[28px]">
                        {roleplay.name}
                      </h1>
                    </Link>
                  )}
                </div>
              </div>
            </div>
            <div className="flex items-end flex-shrink-0">
              {!loading && roleplay && (
                <div className="flex gap-1">
                  {isAdmin ? (
                    <>
                      <Button
                        intent="secondary"
                        className="h-10"
                        onClick={() => {
                          setAssignScenarioOpen(true);
                        }}
                      >
                        Assign
                      </Button>
                      <AssignScenarioDialog
                        open={assignScenarioOpen}
                        onOpenChange={() => setAssignScenarioOpen(false)}
                        collectionId={roleplay.id}
                      />
                    </>
                  ) : (
                    <div className="mb-8" />
                  )}
                  {!currentWorkspace.isPersonal && (
                    <RoleplayShare roleplay={roleplay} />
                  )}
                  <RoleplayMenu
                    onDelete={() => setCurrentActiveModal("deleteRoleplay")}
                    onEdit={() => setCurrentActiveModal("editCollection")}
                    canEdit={roleplay.canEdit ?? false}
                    onClone={() => setCurrentActiveModal("cloneCollection")}
                  />
                </div>
              )}
            </div>
          </div>
        </Container>
      </div>
      {!loading && <RoleplayLimitBanner page="roleplayDetail" />}

      {/* Modals */}
      {roleplay && (
        <>
          <CreateCollectionDialog
            isOpen={currentActiveModal === "editCollection"}
            roleplay={roleplay}
            onClose={() => setCurrentActiveModal(undefined)}
            onFinished={() => {
              setCurrentActiveModal(undefined);
            }}
          />
          <RoleplayDeleteConfirm
            open={currentActiveModal === "deleteRoleplay"}
            onClose={() => setCurrentActiveModal(undefined)}
            roleplay={roleplay}
            onFinished={() => {
              setCurrentActiveModal(undefined);
            }}
          />
          <CloneCollectionDialog
            open={currentActiveModal === "cloneCollection"}
            onClose={() => setCurrentActiveModal(undefined)}
            collection={roleplay}
          />
        </>
      )}
    </>
  );
}

const StyledBreadcrumb = styled.div`
  max-width: 200px;
  color: ${SUBTEXT_COLOR};
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  display: inline-block;
  font-size: 14px;

  a {
    font-weight: 600;
    color: ${SUBTEXT_COLOR};
  }
`;
